import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { CONTAINS_LETTER } from '../../lib/validators'
import { Plus, Pencil, Trash2, Building2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '../../components/ui/Button'
import { TextField, TextAreaField, FieldLabel } from '../../components/ui/Field'
import { Modal } from '../../components/ui/Modal'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { Breadcrumb } from '../../components/Breadcrumb'
import { FilterRow } from '../../components/filters/FilterRow'
import { DataTable } from '../../components/table/DataTable'
import { RowActionsMenu } from '../../components/table/RowActionsMenu'
import { api, extractApiError } from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import {
  departmentList,
  departmentSiblingList,
  workspaceListResponse,
} from '../../lib/types'

const departmentSchema = z.object({
  /** Workspaces this Department should be added to. Departments are
   *  workspace-scoped, so the Add dialog accepts one or more — picking
   *  multiple creates one independent row per Workspace in a single
   *  submit. `.min(1)` runs at the Zod layer so the "Pick at least
   *  one Workspace" error shows up alongside the Department Name
   *  error on the same submit (the old behaviour relied on a runtime
   *  `setError` call that only fired AFTER Zod passed — which never
   *  happens when name is also empty). Edit mode keeps working
   *  because the locked current-workspace row is always in the array. */
  workspaceIds: z
    .array(z.string().uuid())
    .min(1, 'Pick at least one Workspace.')
    .default([]),
  name: z
    .string({ message: 'Department Name is required.' })
    .trim()
    .min(1, 'Department Name is required.')
    .min(2, 'Department Name must be at least 2 characters.')
    .max(120, 'Department Name must be at most 120 characters.')
    .regex(
      CONTAINS_LETTER,
      'Department Name must contain at least one letter.',
    ),
  description: z
    .string()
    .trim()
    .max(500, 'Description must be at most 500 characters.')
    .optional()
    .or(z.literal('')),
  /** Mirrors `departments.status` in the schema. New departments default
   *  to 'active' via `defaultValues` on the form. The DB enum is
   *  `'active' | 'inactive'` (renamed from the legacy `'archived'` to
   *  match the rest of the admin surface). */
  status: z.enum(['active', 'inactive']),
  /** Edit-only. When ticked, the same name/description/status changes
   *  fan out to sibling Department rows in other Workspaces (matched
   *  by the row's *original* name, case-insensitive). Default false
   *  preserves the existing single-row behaviour. */
  applyToOtherWorkspaces: z.boolean().default(false),
})

export function DepartmentsPage() {
  const qc = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  // Edit mode only: every Workspace that ALREADY has a Department with
  // the same name (the same-name siblings created via the multi-
  // Workspace picker). They get pre-ticked + locked so the popup
  // truthfully shows where this Department lives — not just the single
  // row being edited — which is what made "edit shows only one
  // Workspace" look like data loss. The current row's own Workspace is
  // always in here too.
  const [editWorkspaceIds, setEditWorkspaceIds] = useState([])
  const [confirmDelete, setConfirmDelete] = useState(null)

  // Departments are workspace-scoped — pick a workspace to view/manage
  // its independent department list. Defaults to the first workspace the
  // admin belongs to.
  const workspacesQuery = useQuery({
    queryKey: ['workspaces', 'mine'],
    queryFn: async () => {
      const res = await api.get('/workspaces/mine')
      return workspaceListResponse.parse(res.data)
    },
  })
  const workspaces = workspacesQuery.data?.data ?? []
  // Workspace de-emphasis (Phase 1): with a single Workspace there's nothing
  // to filter by and every row shares the same Workspace, so the Workspace
  // filter and the "Workspace Name" column are hidden. The page keeps its
  // default "All Workspaces" view, which for one Workspace just lists that
  // Workspace's Departments.
  const hasMultipleWorkspaces = workspaces.length > 1
  // The tenant's only Workspace (when there's exactly one). With a single
  // Workspace the Add/Edit dialog hides the Workspace picker entirely and
  // silently attaches the Department to this one — the admin never has to
  // pick a Workspace because everything lives in the default Workspace.
  const soleWorkspaceId = hasMultipleWorkspaces ? null : (workspaces[0]?.id ?? null)

  // Sentinel value for the dropdown's "All Workspaces" choice. Picking
  // it tells the page to fan out a request per workspace and merge the
  // results — each row keeps its origin workspace name so the
  // "Workspace Name" column lets the admin see which workspace each
  // department belongs to without flipping the filter.
  const ALL_WORKSPACES = '__all__'

  // Default the Workspace filter to "All Workspaces" so the admin sees
  // every workspace's departments on first load — not just the first
  // workspace's. The effect only re-defaults when the current pick is a
  // specific workspace that no longer exists (e.g. the admin was removed
  // from it); "All Workspaces" is sticky.
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(ALL_WORKSPACES)
  useEffect(() => {
    if (workspaces.length === 0) return
    if (
      activeWorkspaceId === ALL_WORKSPACES ||
      (activeWorkspaceId && workspaces.some((w) => w.id === activeWorkspaceId))
    ) {
      return
    }
    setActiveWorkspaceId(ALL_WORKSPACES)
  }, [workspaces, activeWorkspaceId])

  const isAllWorkspaces = activeWorkspaceId === ALL_WORKSPACES

  // Single-workspace query — only fires when a specific workspace is
  // picked. Disabled in "All" mode so we don't waste a request that
  // would be ignored.
  const singleQuery = useQuery({
    queryKey: ['departments', activeWorkspaceId],
    queryFn: async () => {
      const res = await api.get(
        `/workspaces/${activeWorkspaceId}/departments`,
      )
      return departmentList.parse(res.data)
    },
    enabled: Boolean(activeWorkspaceId) && !isAllWorkspaces,
  })

  // "All Workspaces" query — one request per workspace in parallel,
  // each row stamped with its workspace name so the table column can
  // distinguish them. Cache key includes the sorted list of workspace
  // ids so adding a workspace (or being removed from one) triggers a
  // refetch on the All view.
  const allWorkspacesKey = useMemo(
    () => workspaces.map((w) => w.id).sort().join(','),
    [workspaces],
  )
  const allQuery = useQuery({
    queryKey: ['departments', '__all__', allWorkspacesKey],
    queryFn: async () => {
      const results = await Promise.all(
        workspaces.map(async (w) => {
          try {
            const res = await api.get(`/workspaces/${w.id}/departments`)
            const parsed = departmentList.parse(res.data)
            return (parsed.data ?? []).map((d) => ({
              ...d,
              __workspaceId: w.id,
              __workspaceName: w.name,
            }))
          } catch {
            // Drop the failing workspace silently — the rest of the
            // list still renders. The admin can switch back to that
            // workspace specifically to see the real error.
            return []
          }
        }),
      )
      return { data: results.flat() }
    },
    enabled: isAllWorkspaces && workspaces.length > 0,
  })

  const data = isAllWorkspaces ? allQuery.data : singleQuery.data
  const isLoading = isAllWorkspaces ? allQuery.isLoading : singleQuery.isLoading
  const isError = isAllWorkspaces ? allQuery.isError : singleQuery.isError
  const error = isAllWorkspaces ? allQuery.error : singleQuery.error

  const allRows = data?.data ?? []
  // Free-text search across department name + description so admins
  // can find a department by either field. Applied BEFORE sort so
  // the visible subset orders correctly and pagination follows.
  const [search, setSearch] = useState('')
  const rawRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return allRows
    return allRows.filter(
      (r) =>
        String(r.name ?? '').toLowerCase().includes(q) ||
        String(r.description ?? '').toLowerCase().includes(q),
    )
  }, [allRows, search])
  // Resolved name of the workspace whose departments are being
  // listed — surfaced in the table's "Workspace Name" column so the
  // user can see which workspace each row belongs to without having
  // to glance back at the filter dropdown above. In "All" mode this
  // fallback is unused because each row carries its own
  // `__workspaceName`.
  const activeWorkspaceName =
    workspaces.find((w) => w.id === activeWorkspaceId)?.name ?? '—'

  // In "All Workspaces" mode the same Department name legitimately
  // exists once per Workspace, which previously rendered as N
  // near-identical rows (the "shown twice" report). Collapse those into
  // a single grouped row whose Workspace column opens a dropdown listing
  // every Workspace the Department lives in. Members are summed across
  // the group; Status/Created On show a representative value (the shared
  // status, or "Mixed" if they diverge, and the earliest creation).
  // Single-Workspace mode keeps the flat rows — a Workspace's own list
  // is unique by name, so there's nothing to collapse.
  const displayRows = useMemo(() => {
    if (!isAllWorkspaces) return rawRows
    const groups = new Map()
    for (const r of rawRows) {
      const key = String(r.name ?? '').trim().toLowerCase()
      const bucket = groups.get(key)
      if (bucket) bucket.push(r)
      else groups.set(key, [r])
    }
    return [...groups.entries()].map(([key, bucket]) => {
      const primary = bucket[0]
      const wsList = bucket.map((r) => ({
        workspaceId: r.__workspaceId,
        workspaceName: r.__workspaceName,
      }))
      const memberCount = bucket.reduce(
        (sum, r) => sum + Number(r.memberCount ?? 0),
        0,
      )
      const statuses = new Set(bucket.map((r) => r.status ?? 'active'))
      const status = statuses.size === 1 ? [...statuses][0] : 'mixed'
      // First non-empty description across the group — synced edits keep
      // these identical, so this just avoids a stray "—" when one copy
      // has text and another doesn't.
      const description =
        bucket.map((r) => r.description?.trim()).find(Boolean) ?? ''
      // Earliest creation marks when the Department first appeared.
      const createdAt =
        bucket
          .map((r) => r.createdAt)
          .filter(Boolean)
          .sort()[0] ?? primary.createdAt
      return {
        id: `group:${key}`,
        isGroup: true,
        name: primary.name,
        description,
        status,
        memberCount,
        createdAt,
        __workspaces: wsList,
        __rows: bucket,
        primaryRow: primary,
      }
    })
  }, [rawRows, isAllWorkspaces])

  // DataTable column config. It owns sorting / pagination / columns /
  // export; the Workspace filter + search stay in the FilterRow above
  // (Workspace switches the data source, so it can't live in the table).
  const columns = useMemo(
    () => {
      const cols = [
        {
          key: 'name',
          header: 'Department Name',
          pinnable: true,
          minWidth: 220,
          sortAccessor: (r) => (r.name ?? '').toLowerCase(),
          searchAccessor: (r) => `${r.name ?? ''} ${r.description ?? ''}`,
          exportValue: (r) => r.name,
          render: (r) => (
            <span className="text-[color:var(--color-ink-strong)] font-medium">
              {r.name}
            </span>
          ),
        },
        {
          key: 'description',
          header: 'Description',
          grow: true,
          minWidth: 240,
          cellClassName: 'whitespace-normal break-words',
          sortAccessor: (r) => (r.description ?? '').toLowerCase(),
          exportValue: (r) => r.description ?? '',
          render: (r) =>
            r.description?.trim() || (
              <span className="text-[color:var(--color-ink-subtle)]">—</span>
            ),
        },
        ...(hasMultipleWorkspaces
          ? [
              {
                key: 'workspace',
                header: 'Workspace Name',
                minWidth: 180,
                sortable: false,
                exportValue: (r) =>
                  r.isGroup
                    ? r.__workspaces.map((w) => w.workspaceName).join(', ')
                    : r.__workspaceName ?? activeWorkspaceName,
                render: (r) =>
                  r.isGroup ? (
                    <div className="flex flex-wrap gap-1">
                      {r.__workspaces.map((w) => (
                        <span
                          key={w.workspaceId}
                          className="inline-block max-w-[150px] truncate rounded-full border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-2.5 py-0.5 text-xs font-medium text-[color:var(--color-ink)]"
                          title={w.workspaceName}
                        >
                          {w.workspaceName}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-[color:var(--color-ink)]">
                      {r.__workspaceName ?? activeWorkspaceName}
                    </span>
                  ),
              },
            ]
          : []),
        {
          key: 'memberCount',
          header: 'Members',
          minWidth: 100,
          sortAccessor: (r) => Number(r.memberCount ?? r.members?.length ?? 0),
          exportValue: (r) => String(r.memberCount ?? 0),
          render: (r) => (
            <span className="text-[color:var(--color-ink)]">
              {r.memberCount}
            </span>
          ),
        },
        {
          key: 'status',
          header: 'Status',
          minWidth: 120,
          sortAccessor: (r) => r.status ?? '',
          exportValue: (r) =>
            r.status === 'mixed'
              ? 'Mixed'
              : r.status === 'active'
                ? 'Active'
                : 'Inactive',
          render: (r) => <DeptStatusPill status={r.status} />,
        },
        {
          key: 'createdAt',
          header: 'Created On',
          minWidth: 160,
          sortAccessor: (r) => (r.createdAt ? new Date(r.createdAt) : null),
          exportValue: (r) => formatDateTime(r.createdAt),
          render: (r) => (
            <span className="whitespace-nowrap text-[color:var(--color-ink-muted)]">
              {formatDateTime(r.createdAt)}
            </span>
          ),
        },
        {
          key: 'actions',
          header: 'Actions',
          pinnedRight: true,
          align: 'center',
          minWidth: 72,
          sortable: false,
          exportable: false,
          hideable: false,
          render: (r) => (
            <RowActionsMenu
              label={`Actions for ${r.name}`}
              items={[
                {
                  key: 'edit',
                  label: 'Edit',
                  icon: Pencil,
                  onClick: () => openEdit(r.isGroup ? r.primaryRow : r),
                },
                {
                  key: 'delete',
                  label: 'Delete',
                  icon: Trash2,
                  danger: true,
                  separatorBefore: true,
                  onClick: () => setConfirmDelete(r),
                },
              ]}
            />
          ),
        },
      ]
      return cols
    },
    // openEdit / setConfirmDelete are stable enough (they close over
    // stable setters), so the column set only needs to rebuild when the
    // Workspace column's visibility or resolved name changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasMultipleWorkspaces, activeWorkspaceName],
  )

  const form = useForm({
    resolver: zodResolver(departmentSchema),
    defaultValues: {
      workspaceIds: [],
      name: '',
      description: '',
      status: 'active',
      applyToOtherWorkspaces: false,
    },
  })
  // Watched on every render so the checkbox list reflects toggles.
  const selectedWorkspaceIds = form.watch('workspaceIds') ?? []

  function toggleWorkspaceId(id) {
    const current = form.getValues('workspaceIds') ?? []
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id]
    form.setValue('workspaceIds', next, { shouldValidate: true, shouldDirty: true })
    // Clear any prior "select at least one" error as soon as the admin
    // ticks something.
    if (next.length > 0 && form.formState.errors.workspaceIds) {
      form.clearErrors('workspaceIds')
    }
  }

  function openCreate() {
    setEditing(null)
    form.reset({
      // Single Workspace: silently pre-attach to the only Workspace — the
      // picker is hidden, so the admin never picks one. Multiple
      // Workspaces: no workspace is pre-selected, the admin explicitly
      // ticks which workspace(s) the new department lands in. (We don't
      // auto-tick the page's current workspace there, which previously
      // produced accidental departments when the admin didn't notice it.)
      workspaceIds: soleWorkspaceId ? [soleWorkspaceId] : [],
      name: '',
      description: '',
      status: 'active',
      applyToOtherWorkspaces: false,
    })
    setModalOpen(true)
  }

  // Dashboard shortcut: `?action=new` opens the Add Department dialog
  // on mount. Same pattern as WorkspacesPage / TenantsPage — `useRef`
  // guards React 18 StrictMode double-fire and we strip the param so
  // refresh / back-button doesn't re-open the dialog.
  const [searchParams, setSearchParams] = useSearchParams()
  const handledActionRef = useRef(false)
  useEffect(() => {
    if (handledActionRef.current) return
    if (searchParams.get('action') !== 'new') return
    handledActionRef.current = true
    openCreate()
    const next = new URLSearchParams(searchParams)
    next.delete('action')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams])

  async function openEdit(row) {
    setEditing(row)
    // Discover EVERY Workspace that already has a Department with this
    // name (the same-name siblings the multi-Workspace picker creates),
    // so the popup pre-ticks + locks all of them — not just the row's
    // own Workspace. Without this the edit popup showed a single
    // Workspace even when the Department lived in several, which read as
    // data loss. Falls back to just the current Workspace if the sibling
    // lookup fails, so an edit is never blocked.
    let existingIds = [row.workspaceId]
    try {
      const res = await api.get('/departments', { params: { name: row.name } })
      const siblings = departmentSiblingList.parse(res.data).data
      existingIds = [
        ...new Set([row.workspaceId, ...siblings.map((s) => s.workspaceId)]),
      ]
    } catch {
      /* lookup failed — keep just the current Workspace */
    }
    setEditWorkspaceIds(existingIds)
    form.reset({
      // Pre-tick the row's own Workspace AND every other Workspace this
      // Department already exists in (all locked); admin can still tick
      // brand-new Workspaces to fan out further copies.
      workspaceIds: existingIds,
      name: row.name,
      description: row.description ?? '',
      status: row.status ?? 'active',
      applyToOtherWorkspaces: false,
    })
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
    setEditing(null)
    setEditWorkspaceIds([])
    form.reset()
  }

  const saveMutation = useMutation({
    mutationFn: async (values) => {
      // Status is only meaningful on edit. The Add dialog hides the
      // toggle and defaults to 'active', so we don't send it on create
      // to keep the payload minimal.
      const payload = {
        name: values.name.trim(),
        // Send the (trimmed) description, empty string included, so clearing
        // it on edit reaches the backend and resets it to null. `|| undefined`
        // dropped the key, which the update service reads as "leave unchanged".
        description: values.description?.trim() ?? '',
        // Status applies to BOTH create + edit so the admin can ship
        // a department "Inactive from day one". Backend defaults to
        // 'active' when omitted, but we always send it now that the
        // Add dialog also exposes the dropdown.
        status: values.status,
      }
      if (editing) {
        // Edit uses the row's existing workspaceId — moving a Department
        // between Workspaces isn't supported.
        const wsId = editing.workspaceId
        // Always update the row the admin is editing first. If the
        // primary PATCH fails (e.g. duplicate name in this workspace
        // after a rename) we throw — onError surfaces the message
        // inline; we don't fan out partial changes.
        await api.patch(
          `/workspaces/${wsId}/departments/${editing.id}`,
          payload,
        )

        // Fan-out: any BRAND-NEW Workspace ticked in the picker gets a
        // NEW Department row with the same name and description. We
        // exclude both the current row's Workspace AND the workspaces
        // the Department already exists in (`editWorkspaceIds`, which the
        // popup pre-ticked + locked) so we don't fire pointless 409s for
        // siblings that are already there. 409 (a race) → still skipped.
        const additionalIds = (values.workspaceIds ?? []).filter(
          (id) => id !== editing.workspaceId && !editWorkspaceIds.includes(id),
        )
        const fanCreated = []
        const fanSkipped = []
        const fanFailed = []
        if (additionalIds.length > 0) {
          const fanPayload = {
            name: values.name.trim(),
            description: values.description?.trim() || undefined,
          }
          const fanSettled = await Promise.allSettled(
            additionalIds.map((id) =>
              api.post(`/workspaces/${id}/departments`, fanPayload),
            ),
          )
          fanSettled.forEach((r, i) => {
            if (r.status === 'fulfilled') {
              fanCreated.push(additionalIds[i])
            } else if (r.reason?.response?.status === 409) {
              fanSkipped.push({ workspaceId: additionalIds[i], reason: r.reason })
            } else {
              fanFailed.push({ workspaceId: additionalIds[i], reason: r.reason })
            }
          })
        }

        // Sync existing same-name siblings only if the admin opted in.
        if (!values.applyToOtherWorkspaces) {
          return {
            mode: 'edit',
            fanCreated,
            fanSkipped,
            fanFailed,
          }
        }

        // Find sibling rows by the *original* name — the rename (if
        // any) hasn't propagated yet, and we want to match the name
        // the admin saw on the row at edit-time.
        const lookupName = editing.name
        const siblingsRes = await api.get('/departments', {
          params: { name: lookupName },
        })
        const siblings = departmentSiblingList.parse(siblingsRes.data).data
        // Skip the primary row — already updated above.
        const targets = siblings.filter((s) => s.id !== editing.id)

        if (targets.length === 0) {
          return { mode: 'edit-sync', synced: [], failed: [], total: 0 }
        }

        const settled = await Promise.allSettled(
          targets.map((s) =>
            api.patch(
              `/workspaces/${s.workspaceId}/departments/${s.id}`,
              payload,
            ),
          ),
        )
        const synced = []
        const failed = []
        settled.forEach((r, i) => {
          const t = targets[i]
          if (r.status === 'fulfilled') {
            synced.push({ id: t.id, workspaceName: t.workspaceName })
          } else {
            failed.push({
              id: t.id,
              workspaceName: t.workspaceName,
              reason: r.reason,
            })
          }
        })
        return {
          mode: 'edit-sync',
          synced,
          failed,
          total: targets.length,
        }
      }
      // Create — fan out one POST per chosen Workspace so the same
      // Department name lands in each. allSettled so a duplicate-name
      // failure in one workspace doesn't drop the rest.
      // 409 (Conflict) is treated as a benign "already existed in this
      // Workspace" — counted as `skipped`, not `failed`. The user's
      // intent is "make this dept exist in these Workspaces", and a
      // pre-existing row already satisfies that.
      const ids = values.workspaceIds ?? []
      const settled = await Promise.allSettled(
        ids.map((wsId) =>
          api.post(`/workspaces/${wsId}/departments`, payload),
        ),
      )
      const created = []
      const skipped = []
      const failed = []
      settled.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          created.push(ids[i])
        } else if (r.reason?.response?.status === 409) {
          skipped.push({ workspaceId: ids[i], reason: r.reason })
        } else {
          failed.push({ workspaceId: ids[i], reason: r.reason })
        }
      })
      return { mode: 'create', created, skipped, failed, total: ids.length }
    },
    onSuccess: (result) => {
      if (result?.mode === 'edit') {
        const { fanCreated = [], fanSkipped = [], fanFailed = [] } = result
        const fanTotal = fanCreated.length + fanSkipped.length + fanFailed.length
        if (fanTotal === 0) {
          toast.success('Department updated successfully.')
        } else if (fanFailed.length === 0 && fanCreated.length === 0) {
          toast.success(
            `Department updated. Already existed in ${fanSkipped.length} additional Workspace${fanSkipped.length === 1 ? '' : 's'}.`,
          )
        } else if (fanFailed.length === 0) {
          toast.success(
            fanSkipped.length === 0
              ? `Department updated and added in ${fanCreated.length} more Workspace${fanCreated.length === 1 ? '' : 's'}.`
              : `Department updated, added in ${fanCreated.length}, already existed in ${fanSkipped.length}.`,
          )
        } else {
          const firstFailMsg = extractApiError(fanFailed[0].reason).message
          toast.warning(
            `Department updated. ${fanCreated.length + fanSkipped.length} of ${fanTotal} additional Workspaces succeeded. ${fanFailed.length} failed: ${firstFailMsg}`,
          )
        }
        closeModal()
        void qc.invalidateQueries({ queryKey: ['departments'] })
        return
      }
      if (result?.mode === 'edit-sync') {
        const { synced, failed, total } = result
        // total counts siblings only (primary already updated).
        if (total === 0) {
          toast.success(
            'Department updated. No matching Departments found in other Workspaces.',
          )
        } else if (failed.length === 0) {
          toast.success(
            `Department updated in ${synced.length + 1} of ${total + 1} matching Departments.`,
          )
        } else {
          const firstFailMsg = extractApiError(failed[0].reason).message
          toast.warning(
            `Updated in ${synced.length + 1} of ${total + 1} Workspaces. ${failed.length} failed (${failed[0].workspaceName}: ${firstFailMsg})`,
          )
        }
        closeModal()
        void qc.invalidateQueries({ queryKey: ['departments'] })
        return
      }
      const { created, skipped, failed, total } = result
      // Five-way breakdown so the toast accurately tells the admin
      // what landed where without conflating "newly added" with
      // "already existed". Mirrors the spec at:
      //   1. All created       → green "added in N Workspaces"
      //   2. Some created + some skipped → green "added in X; already existed in Y"
      //   3. All skipped       → AMBER "already existed … nothing was added"
      //      (nothing was created, so a green tick would misreport a
      //      rejected duplicate as a successful add)
      //   4. Some success + some failed → warning "added/already in X of N. Y failed"
      //   5. All failed        → red error with the cause
      const effective = created.length + skipped.length

      if (failed.length === 0 && skipped.length === 0) {
        // 1. All newly created.
        toast.success(
          total === 1
            ? 'Department added successfully.'
            : `Department added in ${total} Workspaces successfully.`,
        )
      } else if (failed.length === 0 && created.length > 0 && skipped.length > 0) {
        // 2. Mix of newly created + already existing — both count
        //    as "this dept now exists there", but be honest about it.
        toast.success(
          `Department added in ${created.length} Workspace${created.length === 1 ? '' : 's'}; already existed in ${skipped.length}.`,
        )
      } else if (failed.length === 0 && created.length === 0 && skipped.length > 0) {
        // 3. All ticked Workspaces already had this dept — so NOTHING was
        //    created. Deliberately not `toast.success`: a green tick reads
        //    as "your Department was added", when in fact the request was
        //    rejected as a duplicate and the list is unchanged. Amber says
        //    "nothing went wrong, but nothing happened either", which is
        //    exactly the situation. (Case 2 above keeps green — something
        //    genuinely WAS created there.)
        toast.warning(
          skipped.length === 1
            ? 'Department already existed in this Workspace — nothing was added.'
            : `Department already existed in all ${skipped.length} Workspaces — nothing was added.`,
        )
      } else if (failed.length > 0 && effective === 0) {
        // 5. Nothing landed. Show a single error toast with the cause.
        const firstMsg = extractApiError(failed[0].reason).message
        toast.error(firstMsg)
        return
      } else {
        // 4. Partial: some succeeded (created or skipped), some failed.
        const firstFailMsg = extractApiError(failed[0].reason).message
        toast.warning(
          `Added / already existed in ${effective} of ${total} Workspaces. ${failed.length} failed: ${firstFailMsg}`,
        )
      }

      // Hint: the Departments page only shows one Workspace at a time,
      // so a multi-Workspace add is invisible until the admin switches
      // the filter. Surfaced as a quieter follow-up toast only when
      // more than one Workspace was involved (created OR skipped).
      if (effective > 1) {
        toast.info(
          'Switch the Workspace filter above to view Departments in each Workspace.',
        )
      }
      closeModal()
      void qc.invalidateQueries({ queryKey: ['departments'] })
      // Jump the page to the first newly-added workspace so the admin
      // sees their new row immediately, unless they're already viewing one
      // of the workspaces it landed in.
      // In "All Workspaces" mode we DON'T jump — the merged view
      // already shows every workspace, so the new row appears in-place
      // without forcing a context switch.
      if (
        !isAllWorkspaces &&
        created.length > 0 &&
        !created.includes(activeWorkspaceId)
      ) {
        setActiveWorkspaceId(created[0])
      }
    },
    onError: (err) => {
      // Only fires on Edit (Create uses allSettled and never throws).
      const { message, fields } = extractApiError(err)
      let mapped = false
      if (fields) {
        for (const [name, msg] of Object.entries(fields)) {
          if (name in form.getValues()) {
            form.setError(name, { message: msg })
            mapped = true
          }
        }
      }
      if (!mapped) toast.error(message)
    },
  })

  /** Submit gate — manually enforce min-1 Workspace selection on Add
   *  before letting the mutation run. zod can't conditionally require
   *  workspaceIds based on edit-mode, so we do it here. */
  function onFormSubmit(values) {
    if (!editing && (values.workspaceIds?.length ?? 0) === 0) {
      form.setError('workspaceIds', {
        message: 'Please select at least one Workspace.',
      })
      return
    }
    saveMutation.mutate(values)
  }

  const deleteMutation = useMutation({
    // Caller passes the full row so we can route the DELETE to the
    // correct workspace even in "All Workspaces" mode (where
    // `activeWorkspaceId` is the sentinel `__all__` and would 404).
    // The row's `__workspaceId` is set by the All-mode fetcher;
    // single-workspace rows fall back to `activeWorkspaceId`.
    mutationFn: async (target) => {
      // Grouped row ("All Workspaces" view): the Department exists in
      // several Workspaces, so deleting the group removes every copy —
      // one DELETE per Workspace it lives in. allSettled isn't needed;
      // any single failure surfaces via onError and the rest still ran.
      if (target.isGroup) {
        await Promise.all(
          target.__rows.map((row) =>
            api.delete(`/workspaces/${row.__workspaceId}/departments/${row.id}`),
          ),
        )
        return
      }
      const wsId = target.__workspaceId || activeWorkspaceId
      await api.delete(`/workspaces/${wsId}/departments/${target.id}`)
    },
    onSuccess: () => {
      toast.success('Department deleted successfully.')
      setConfirmDelete(null)
      // Invalidate BOTH the single-workspace cache and the All cache
      // so a delete from the merged view refreshes everywhere.
      void qc.invalidateQueries({ queryKey: ['departments'] })
    },
    onError: (err) => {
      toast.error(extractApiError(err).message)
    },
  })

  const noWorkspaces = !workspacesQuery.isLoading && workspaces.length === 0

  // A filter is "active" when the search box is non-empty or (on multi-
  // workspace tenants) the Workspace filter isn't "All Workspaces".
  // Drives the Clear-all-filters button. Page reset is already handled
  // by the effect that watches `search` / `activeWorkspaceId`.
  const hasAnyFilter =
    search.trim() !== '' ||
    (hasMultipleWorkspaces && activeWorkspaceId !== ALL_WORKSPACES)

  function clearFilters() {
    setSearch('')
    setActiveWorkspaceId(ALL_WORKSPACES)
  }

  // Context-aware empty copy — distinguishes a genuinely empty tenant
  // from a search miss or a workspace with no departments, so a failed
  // search never reads as "the company has no departments".
  const emptyState = useMemo(() => {
    if (allRows.length === 0)
      return {
        icon: Building2,
        title: 'No departments yet',
        body: 'Add a department to start grouping teammates by function — engineering, ops, support, etc.',
      }
    if (search.trim() !== '')
      return {
        icon: Building2,
        title: 'No department found with this name',
        body: `Nothing matches “${search.trim().slice(0, 40)}”. Check the spelling, or clear the search to see every department.`,
      }
    if (hasAnyFilter)
      return {
        icon: Building2,
        title: 'No departments in this Workspace',
        body: 'This Workspace has no departments yet. Switch to All Workspaces to see the ones that exist elsewhere.',
      }
    return {
      icon: Building2,
      title: 'No departments yet',
      body: 'Add a department to start grouping teammates by function — engineering, ops, support, etc.',
    }
  }, [allRows.length, search, hasAnyFilter])

  return (
    <div>
      <Breadcrumb current="Departments" />

      {/* Stacks below `sm` so the action button is never pushed off the right edge on a phone. */}
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)]">
            Departments
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            {hasMultipleWorkspaces
              ? 'Group members into departments — each Workspace has its own independent list, so the same name can be reused across Workspaces.'
              : 'Group members into departments to organise your team.'}
          </p>
        </div>
        <Button
          onClick={openCreate}
          disabled={!activeWorkspaceId || saveMutation.isPending}
        >
          <Plus size={14} />
          Add Department
        </Button>
      </div>

      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={setSearch}
          placeholder="Search by Department Name or Description"
        />
        {hasMultipleWorkspaces ? (
          <FilterRow.Select
            ariaLabel="Workspace"
            placeholder={workspaces.length === 0 ? 'No workspaces yet' : 'Workspace'}
            value={activeWorkspaceId ?? ''}
            onChange={(v) => setActiveWorkspaceId(v || null)}
            options={
              workspaces.length === 0
                ? [{ value: '', label: 'No workspaces yet' }]
                : [
                    // "All Workspaces" sentinel sits at the top so the
                    // admin's first impulse — "show me everything" — is
                    // a one-click action. Picking it flips the page into
                    // the merged view where each row carries its
                    // own workspace name in the table column.
                    { value: ALL_WORKSPACES, label: 'All Workspaces' },
                    ...workspaces.map((w) => ({ value: w.id, label: w.name })),
                  ]
            }
          />
        ) : null}
        <FilterRow.Spacer />
        <FilterRow.Clear show={hasAnyFilter} onClick={clearFilters} />
      </FilterRow>

      {noWorkspaces ? (
        <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-8 text-center text-sm text-[color:var(--color-ink-muted)]">
          You don&apos;t belong to any Workspaces yet. Create or join a
          Workspace before adding Departments.
        </div>
      ) : (
        <DataTable
          tableId="app-departments"
          columns={columns}
          rows={displayRows}
          getRowId={(r) => r.id}
          loading={isLoading}
          error={isError ? extractApiError(error).message : null}
          onRetry={() =>
            isAllWorkspaces ? allQuery.refetch() : singleQuery.refetch()
          }
          title="Departments"
          exportBaseName="departments"
          exportTitle="Departments"
          searchable={false}
          selectable
          defaultSort={[{ key: 'name', dir: 'asc' }]}
          emptyState={emptyState}
        />
      )}

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editing ? 'Edit Department' : 'Add Department'}
        size="ml"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={closeModal}
              disabled={saveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              form="department-form"
              type="submit"
              loading={saveMutation.isPending}
            >
              {editing ? 'Save Changes' : 'Add'}
            </Button>
          </>
        }
      >
        <form
          id="department-form"
          onSubmit={form.handleSubmit(onFormSubmit)}
          noValidate
          className="space-y-4"
        >
          {/* Workspaces picker — shown only when the tenant has more than
           *  one Workspace. With a single (default) Workspace there's
           *  nothing to choose, so the picker is hidden and the Department
           *  is silently attached to that Workspace (openCreate pre-fills
           *  workspaceIds). Multi-Workspace Add: tick one or more; the Add
           *  action creates one independent Department row per ticked
           *  Workspace in a single submit. Edit keeps the row's own
           *  Workspace locked because moving a Department between
           *  Workspaces would dangle every Space and member tagged with
           *  it. */}
          {hasMultipleWorkspaces ? (
          <div>
            <FieldLabel
              label="Workspaces"
              required
              htmlFor="department-workspaces-list"
            />
            <div
              id="department-workspaces-list"
              role="group"
              aria-label="Workspaces"
              className={`max-h-60 overflow-auto rounded-md border bg-[color:var(--color-card)] ${
                form.formState.errors.workspaceIds?.message
                  ? 'border-[color:var(--color-priority-urgent)]'
                  : 'border-[color:var(--color-rule)]'
              }`}
            >
              {(() => {
                // Picker eligibility:
                //   - Add mode: only Active workspaces. Inactive ones
                //     can't accept new departments (the backend
                //     rejects writes via `assertWorkspaceWritable`),
                //     so hiding them avoids a confusing
                //     "BadRequest: workspace is Inactive" toast.
                //   - Edit mode: same filter, but the row's own
                //     workspace stays in the list (and locked) so
                //     the user sees the parent even if it's Inactive.
                const selectable = workspaces.filter(
                  (w) =>
                    w.status === 'active' ||
                    (editing &&
                      (w.id === editing.workspaceId ||
                        editWorkspaceIds.includes(w.id))),
                )
                if (selectable.length === 0) {
                  return (
                    <div className="px-3 py-3 text-sm text-[color:var(--color-ink-muted)]">
                      No Active Workspaces available — reactivate or
                      create one first.
                    </div>
                  )
                }
                return selectable.map((w, idx) => {
                  const checked = selectedWorkspaceIds.includes(w.id)
                  // In Edit mode, the row's own Workspace stays pre-
                  // ticked AND locked — the user can't move a row to a
                  // different Workspace (it'd dangle the Spaces and
                  // members tagged with it). They CAN tick additional
                  // Workspaces to fan out a copy of this dept there.
                  // Lock the current row's Workspace AND every other
                  // Workspace this Department already exists in — both are
                  // facts the admin can't change from this popup (removing
                  // a sibling is done from that sibling's own row). Only
                  // brand-new Workspaces stay tickable, for fan-out.
                  const isCurrent =
                    Boolean(editing) && w.id === editing.workspaceId
                  const existsHere =
                    Boolean(editing) && editWorkspaceIds.includes(w.id)
                  const lockedForEdit = existsHere
                  return (
                    <label
                      key={w.id}
                      className={`flex items-center gap-2 px-3 py-2 ${
                        lockedForEdit
                          ? 'cursor-not-allowed bg-[color:var(--color-canvas)]'
                          : 'cursor-pointer hover:bg-[color:var(--color-canvas)]'
                      } ${
                        idx > 0
                          ? 'border-t border-[color:var(--color-rule)]'
                          : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={lockedForEdit}
                        onChange={() => toggleWorkspaceId(w.id)}
                        className="h-4 w-4 accent-[color:var(--color-accent)] disabled:cursor-not-allowed cursor-pointer"
                      />
                      <span className="text-sm text-[color:var(--color-ink)] flex-1">
                        {w.name}
                      </span>
                      {isCurrent ? (
                        <span className="text-xs text-[color:var(--color-ink-subtle)]">
                          Current
                        </span>
                      ) : existsHere ? (
                        <span className="text-xs text-[color:var(--color-ink-subtle)]">
                          Already added
                        </span>
                      ) : null}
                    </label>
                  )
                })
              })()}
            </div>
            {form.formState.errors.workspaceIds?.message ? (
              <p
                role="alert"
                className="mt-1 text-xs text-[color:var(--color-priority-urgent)]"
              >
                {form.formState.errors.workspaceIds.message}
              </p>
            ) : (
              <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                {editing
                  ? `Workspaces that already have a "${editing.name}" Department are ticked and locked. Tick any additional Workspace to also create one there.`
                  : 'Tick one or more Workspaces. Each creates an independent Department row that you can rename or deactivate separately.'}
              </p>
            )}
          </div>
          ) : null}
          <TextField
            label="Department Name"
            required
            placeholder="Enter a Department Name"
            minLength={2}
            maxLength={80}
            error={form.formState.errors.name?.message}
            {...form.register('name')}
          />
          {/* Shared TextAreaField instead of a hand-rolled <textarea> —
           *  the same swap already made on the Work Roles dialog for this
           *  exact field. It ships `resize-y`, so the box can no longer be
           *  dragged sideways out of the dialog (a bare <textarea> defaults
           *  to `resize: both`), it inherits the global textarea
           *  min-height/max-height cap so it can't be stretched without
           *  limit, and `showCount` surfaces the 500-character cap that was
           *  already enforced but invisible — the user only discovered it by
           *  typing until input silently stopped. Description stays
           *  optional, so there is deliberately no minLength. */}
          <TextAreaField
            id="department-description"
            label="Description"
            placeholder="Enter a Description"
            maxLength={500}
            rows={4}
            showCount
            error={form.formState.errors.description?.message}
            {...form.register('description')}
          />

          {/* Status — shown on BOTH Add and Edit (used to be edit-
           *  only). Defaults to "Active" for new departments, but the
           *  admin can ship one as "Inactive" from day one if they're
           *  staging the structure ahead of time. Archived (= Inactive
           *  to the user) prevents new members being assigned from
           *  the Members invite form (see `activeDepartments` filter
           *  in MembersPage). */}
          <div>
            <FieldLabel label="Status" htmlFor="department-status" />
            <select
              id="department-status"
              {...form.register('status')}
              className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            {form.formState.errors.status?.message ? (
              <p
                role="alert"
                className="mt-1 text-xs text-[color:var(--color-priority-urgent)]"
              >
                {form.formState.errors.status.message}
              </p>
            ) : null}
          </div>

          {/* Cross-Workspace sync toggle — Edit only. When ticked, the
           *  same Name / Description / Status changes fan out to every
           *  Department row in this tenant whose original name matches
           *  this row's (case-insensitive). Default off keeps the
           *  classic single-row Edit behaviour. Hidden when the tenant has
           *  a single Workspace — there are no "other Workspaces" to fan
           *  changes out to. */}
          {editing && hasMultipleWorkspaces ? (
            <label className="flex items-start gap-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2 cursor-pointer">
              <input
                type="checkbox"
                {...form.register('applyToOtherWorkspaces')}
                className="mt-0.5 h-4 w-4 cursor-pointer accent-[color:var(--color-accent)]"
              />
              <span className="text-sm text-[color:var(--color-ink)]">
                Apply these changes to matching Departments in other
                Workspaces
                <span className="block mt-0.5 text-xs text-[color:var(--color-ink-muted)]">
                  Updates every Department named &ldquo;{editing.name}&rdquo;
                  across this company. Each row keeps its own ID, so
                  members and Spaces stay assigned.
                </span>
              </span>
            </label>
          ) : null}
        </form>
      </Modal>

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete Department"
        message={
          confirmDelete
            ? confirmDelete.isGroup && confirmDelete.__workspaces.length > 1
              ? `Are you sure you want to delete "${confirmDelete.name}" from all ${confirmDelete.__workspaces.length} Workspaces? Members assigned here will lose this Department in every Workspace. This action cannot be undone.`
              : `Are you sure you want to delete "${confirmDelete.name}"? Members assigned here will lose this department in this Workspace. This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        confirming={deleteMutation.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          // Pass the whole row so the mutation can route the DELETE to
          // the right workspace in "All Workspaces" mode.
          if (confirmDelete) deleteMutation.mutate(confirmDelete)
        }}
      />
    </div>
  )
}

/** Department status pill. Active (green) / Inactive (red); a grouped
 *  row whose copies disagree shows a neutral "Mixed" pill. Shared visual
 *  vocabulary with the Workspaces / Work Roles status columns. */
function DeptStatusPill({ status }) {
  if (status === 'mixed') {
    return (
      <span className="inline-flex items-center rounded-full border border-[color:var(--color-rule)] px-3 py-1 text-xs font-semibold text-[color:var(--color-ink-muted)]">
        Mixed
      </span>
    )
  }
  return (
    <span
      className={
        'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold text-white border border-transparent dark:border-white/10 ' +
        (status === 'active'
          ? 'bg-[color:var(--color-success)] dark:bg-white/[0.06] dark:text-emerald-300'
          : 'bg-[color:var(--color-priority-urgent)] dark:bg-white/[0.06] dark:text-rose-300')
      }
    >
      {status === 'active' ? 'Active' : 'Inactive'}
    </span>
  )
}
